import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import ClientOnly from 'components/ClientOnly';
import Loader from 'components/Loader';
import api from 'js/api-helper';
import './PowerBiReport.scss';

const REFRESH_MARGIN_MS = 2 * 60 * 1000;
const MIN_RENEWAL_DELAY_MS = 30 * 1000;
const resolveLang = () => {
  const pageLanguage = document.documentElement.lang;
  return !pageLanguage || pageLanguage === 'no' ? 'nb-NO' : pageLanguage;
};

// Number/date formatting is always Norwegian, by agreement, regardless of the report's own language.
const resolveFormatLang = () => 'nb-NO';

export const POWER_BI_REPORT_SIZES = {
  small: 'small',
  medium: 'medium',
  large: 'large',
};

const FULL_PAGE_SIZE = 'full-page';

export const POWER_BI_DISPLAY_OPTIONS = {
  fitToPage: 'FitToPage',
  fitToWidth: 'FitToWidth',
  actualSize: 'ActualSize',
};
const millisecondsUntilExpiry = tokenExpiration =>
  new Date(tokenExpiration) - Date.now();

const PowerBiReport = ({
  tokenUrl,
  size = POWER_BI_REPORT_SIZES.medium,
  isFullPage = false,
  displayOption,
  accessibleTitle,
  filter,
  lang = {
    serverErrorText: 'Serverfeil',
  },
}) => {
  const frameSize = isFullPage ? FULL_PAGE_SIZE : size;
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const embedRef = useRef(null);
  const embedInfoRef = useRef(null);
  const [isRendered, setIsRendered] = useState(false);

  const captureEmbed = embedded => {
    embedRef.current = embedded;

    if (accessibleTitle) embedded?.setComponentTitle(accessibleTitle);
  };

  const handleEmbedError = event => {
    // eslint-disable-next-line no-console
    console.error('[PowerBiReport] embed error:', event?.detail ?? event);
    setIsRendered(true);
  };

  const eventHandlers = new Map([
    ['rendered', () => setIsRendered(true)],
    ['error', handleEmbedError],
  ]);

  useEffect(() => {
    if (!tokenUrl) return undefined;
    let cancelled = false;

    const loadReport = async () => {
      setIsLoading(true);
      setHasError(false);

      try {
        const [embedInfo, { PowerBIEmbed }, { models }] = await Promise.all([
          api.get(tokenUrl),
          import('powerbi-client-react'),
          import('powerbi-client'),
        ]);

        if (cancelled) return;

        embedInfoRef.current = embedInfo;
        setReport({ PowerBIEmbed, models, embedInfo });
      } catch (loadError) {
        if (cancelled) return;

        setHasError(true);

        // eslint-disable-next-line no-console
        console.error('[PowerBiReport] failed to load the report:', loadError);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadReport();

    return () => {
      cancelled = true;
    };
  }, [tokenUrl]);

  useEffect(() => {
    if (!report) return undefined;

    let cancelled = false;
    let timeoutId = null;
    let renewing = false;

    const scheduleRenewal = () => {
      if (cancelled) return;

      clearTimeout(timeoutId);

      timeoutId = setTimeout(
        renewToken,
        Math.max(
          MIN_RENEWAL_DELAY_MS,
          millisecondsUntilExpiry(embedInfoRef.current.tokenExpiration) -
            REFRESH_MARGIN_MS
        )
      );
    };

    async function renewToken() {
      if (cancelled || renewing) return;

      renewing = true;

      try {
        const embedInfo = await api.get(tokenUrl);

        if (cancelled) return;

        await embedRef.current.setAccessToken(embedInfo.embedToken);
        embedInfoRef.current = embedInfo;
      } catch (renewError) {
        // eslint-disable-next-line no-console
        console.error(
          '[PowerBiReport] failed to refresh the embed token:',
          renewError
        );
      } finally {
        renewing = false;
        scheduleRenewal();
      }
    }

    scheduleRenewal();

    const onVisibilityChange = () => {
      if (document.hidden || cancelled) return;

      if (
        millisecondsUntilExpiry(embedInfoRef.current.tokenExpiration) <=
        REFRESH_MARGIN_MS
      ) {
        clearTimeout(timeoutId);
        renewToken();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [report, tokenUrl]);

  if (!isLoading && !report && !hasError) return null;

  return (
    <ClientOnly>
      <div className="power-bi-report">
        <div
          className={cn('power-bi-report__container', {
            'power-bi-report__container_full-page': isFullPage,
          })}
          {...(isFullPage && {
            tabIndex: 0,
            role: 'region',
            'aria-label': accessibleTitle,
          })}
        >
          <div
            className={cn(
              'power-bi-report__frame',
              `power-bi-report__frame_size-${frameSize}`
            )}
          >
            {hasError && (
              <div className="power-bi-report__error">
                <span
                  className={cn('power-bi-report__error-text', {
                    'power-bi-report__error-text_small':
                      !isFullPage && size === POWER_BI_REPORT_SIZES.small,
                  })}
                >
                  {lang.serverErrorText}
                </span>
              </div>
            )}
            {!hasError && !isRendered && (
              <div className="power-bi-report__loading">
                <Loader />
              </div>
            )}
            {!hasError && report && (
              <report.PowerBIEmbed
                embedConfig={{
                  type: 'report',
                  id: report.embedInfo.reportId,
                  embedUrl: report.embedInfo.embedUrl,
                  accessToken: report.embedInfo.embedToken,
                  tokenType: report.models.TokenType.Embed,
                  ...(filter && {
                    filters: [
                      {
                        $schema: 'http://powerbi.com/product/schema#basic',
                        target: {
                          table: filter.table,
                          column: filter.column,
                        },
                        operator: 'In',
                        values: [filter.value],
                        filterType: report.models.FilterType.Basic,
                      },
                    ],
                  }),
                  settings: {
                    panes: {
                      pageNavigation: {
                        visible: false,
                      },
                      ...(filter && {
                        filters: {
                          visible: false,
                        },
                      }),
                    },
                    localeSettings: {
                      language: resolveLang(),
                      formatLocale: resolveFormatLang(),
                    },
                    ...(displayOption && {
                      layoutType: report.models.LayoutType.Custom,
                      customLayout: {
                        displayOption:
                          report.models.DisplayOption[displayOption],
                      },
                    }),
                  },
                }}
                eventHandlers={eventHandlers}
                getEmbeddedComponent={captureEmbed}
                cssClassName={cn('power-bi-report__embed', {
                  'power-bi-report__embed_rendered': isRendered,
                })}
              />
            )}
          </div>
        </div>
      </div>
    </ClientOnly>
  );
};

PowerBiReport.propTypesMeta = {
  isFullPage: 'exclude',
  displayOption: 'exclude',
};

PowerBiReport.propTypes = {
  tokenUrl: PropTypes.string,
  size: PropTypes.oneOf(Object.values(POWER_BI_REPORT_SIZES)),
  isFullPage: PropTypes.bool,
  displayOption: PropTypes.oneOf(Object.values(POWER_BI_DISPLAY_OPTIONS)),
  accessibleTitle: PropTypes.string,
  filter: PropTypes.shape({
    table: PropTypes.string.isRequired,
    column: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  }),
  lang: PropTypes.shape({
    serverErrorText: PropTypes.string.isRequired,
  }).isRequired,
};

export default PowerBiReport;
